<script setup>
import { computed, ref, reactive, nextTick, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import CompanyAPI from 'dashboard/api/companies';
import TicketsAPI from 'dashboard/api/tickets';

const props = defineProps({
  selectedContact: { type: Object, required: true },
});

const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();

// ── Field definitions ─────────────────────────────────────────────────────────
const contactFieldDefs = computed(() =>
  getters['contactFieldDefinitions/getEnabled'].value || []
);

onMounted(() => {
  store.dispatch('contactFieldDefinitions/fetchAll');
  loadTickets();
});

const SYSTEM_FIELDS = [
  { key: 'name', label: 'Full Name', fieldType: 'text' },
  { key: 'email', label: 'Email', fieldType: 'text' },
  { key: 'phoneNumber', label: 'Phone Number', fieldType: 'text' },
  { key: 'companyDomain', label: 'Company Domain', fieldType: 'company_search' },
  { key: 'city', label: 'City', fieldType: 'text', isAdditional: true, additionalKey: 'city' },
  { key: 'countryCode', label: 'Country', fieldType: 'text', isAdditional: true, additionalKey: 'countryCode' },
  { key: 'identifier', label: 'Identifier', fieldType: 'text' },
];

// Keys already covered by SYSTEM_FIELDS — skip any custom field with these keys
const SYSTEM_FIELD_KEYS = new Set([
  'name', 'email', 'phone_number', 'company_domain', 'company_id',
  'city', 'country_code', 'identifier', 'created_at', 'last_activity_at',
  'blocked', 'avatar',
]);

const allFields = computed(() => [
  ...SYSTEM_FIELDS,
  ...contactFieldDefs.value
    .filter(fd => !SYSTEM_FIELD_KEYS.has(fd.field_key))
    .map(fd => ({
      key: fd.field_key,
      label: fd.label,
      fieldType: fd.field_type,
      options: fd.options || [],
      isCustom: true,
      fieldConfig: fd.field_config || {},
    })),
]);

// ── Cell value helpers ────────────────────────────────────────────────────────
const getFieldValue = field => {
  if (field.isCustom) return props.selectedContact.extraFields?.[field.key] ?? '';
  if (field.isAdditional) return props.selectedContact.additionalAttributes?.[field.additionalKey] ?? '';
  return props.selectedContact[field.key] ?? '';
};

const formatValue = (field, raw) => {
  if (raw === '' || raw == null) return '';
  if (field.fieldType === 'checkbox') return raw ? 'Yes' : 'No';
  if (field.fieldType === 'date') {
    const d = new Date(raw);
    return isNaN(d) ? raw : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return String(raw);
};

// ── Inline editing ────────────────────────────────────────────────────────────
const editState = reactive({ key: null });
const editValue = ref('');
const editInputRef = ref(null);

const isEditing = field => editState.key === field.key;

const startEdit = (field, event) => {
  if (field.fieldType === 'company_search') {
    openCompanySearch(event?.currentTarget);
    return;
  }
  if (field.fieldType === 'checkbox' && field.isCustom) {
    toggleCheckbox(field);
    return;
  }
  editState.key = field.key;
  editValue.value = String(getFieldValue(field));
  nextTick(() => editInputRef.value?.focus?.());
};

const cancelEdit = () => { editState.key = null; editValue.value = ''; };

const saveEdit = async () => {
  if (!editState.key) return;
  const key = editState.key;
  const value = editValue.value;
  cancelEdit();

  const field = allFields.value.find(f => f.key === key);
  if (!field) return;

  try {
    if (field.isCustom) {
      await store.dispatch('contacts/update', {
        id: props.selectedContact.id,
        extraFields: { ...(props.selectedContact.extraFields || {}), [field.key]: value },
      });
    } else if (field.isAdditional) {
      await store.dispatch('contacts/update', {
        id: props.selectedContact.id,
        additionalAttributes: { ...(props.selectedContact.additionalAttributes || {}), [field.additionalKey]: value },
      });
    } else {
      await store.dispatch('contacts/update', { id: props.selectedContact.id, [field.key]: value });
    }
    useAlert('Contact updated');
  } catch {
    useAlert('Failed to update contact');
  }
};

const toggleCheckbox = async field => {
  const current = props.selectedContact.extraFields?.[field.key];
  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      extraFields: { ...(props.selectedContact.extraFields || {}), [field.key]: !current },
    });
  } catch {
    useAlert('Failed to update contact');
  }
};

// ── Company domain search ─────────────────────────────────────────────────────
const showCompanySearch = ref(false);
const companySearchQuery = ref('');
const companySearchResults = ref([]);
const companySearchLoading = ref(false);
const companySearchInputRef = ref(null);
const companyDropdownStyle = ref({ top: '0px', left: '0px', width: '220px', maxHeight: '220px', zIndex: 9999 });

const openCompanySearch = (cardEl) => {
  showCompanySearch.value = true;
  companySearchQuery.value = props.selectedContact.companyDomain || '';
  companySearchResults.value = [];
  nextTick(() => {
    if (cardEl) {
      const rect = cardEl.getBoundingClientRect();
      companyDropdownStyle.value = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 220)}px`,
        maxHeight: '220px',
        zIndex: 9999,
      };
    }
    companySearchInputRef.value?.focus();
  });
};

const closeCompanySearch = () => {
  showCompanySearch.value = false;
  companySearchQuery.value = '';
  companySearchResults.value = [];
};

const searchCompanies = debounce(async query => {
  if (!query || query.length < 1) { companySearchResults.value = []; return; }
  companySearchLoading.value = true;
  try {
    const { data } = await CompanyAPI.search(query, 1);
    companySearchResults.value = data.payload || [];
  } catch {
    companySearchResults.value = [];
  } finally {
    companySearchLoading.value = false;
  }
}, 300);

watch(companySearchQuery, val => searchCompanies(val));

const selectCompany = async company => {
  closeCompanySearch();
  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      companyId: company.id,
    });
    useAlert('Company linked');
  } catch {
    useAlert('Failed to link company');
  }
};

const clearCompany = async () => {
  closeCompanySearch();
  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      companyId: null,
    });
    useAlert('Company removed');
  } catch {
    useAlert('Failed to remove company');
  }
};

// ── Company link ──────────────────────────────────────────────────────────────
const openCompany = () => {
  if (!props.selectedContact.companyId) return;
  router.push({
    name: 'company_show',
    params: { accountId: route.params.accountId, companyId: props.selectedContact.companyId },
  });
};

// ── Conversations ─────────────────────────────────────────────────────────────
const conversations = computed(() => {
  const getter = getters['contactConversations/getAllConversationsByContactId'];
  return getter.value ? getter.value(props.selectedContact.id) : [];
});

const openConversation = id => {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: id },
  });
};

// ── Tickets ───────────────────────────────────────────────────────────────────
const tickets = ref([]);
const ticketsLoading = ref(false);

const loadTickets = async () => {
  if (!props.selectedContact?.id) return;
  ticketsLoading.value = true;
  try {
    const { data } = await TicketsAPI.get({ contact_id: props.selectedContact.id });
    tickets.value = data.data?.payload || [];
  } catch {
    tickets.value = [];
  } finally {
    ticketsLoading.value = false;
  }
};

watch(() => props.selectedContact?.id, () => loadTickets(), { immediate: false });

const openTicket = id => {
  router.push({
    name: 'ticket_detail',
    params: { accountId: route.params.accountId, ticketId: id },
  });
};

const ticketStatusClass = status => {
  const map = {
    open: 'bg-green-100 text-green-700',
    closed: 'bg-n-alpha-2 text-n-slate-9',
    resolved: 'bg-blue-100 text-blue-700',
    pending: 'bg-yellow-100 text-yellow-700',
  };
  return map[status] || 'bg-n-alpha-2 text-n-slate-9';
};

const formatDate = ts => {
  if (!ts) return '';
  return new Date(typeof ts === 'number' ? ts * 1000 : ts).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Fields grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="field in allFields"
        :key="field.key"
        class="flex flex-col gap-1 p-3 rounded-lg border border-n-weak transition-colors relative"
        :class="field.fieldType !== 'company_search' ? 'hover:border-n-strong hover:bg-n-alpha-1 cursor-pointer' : 'cursor-pointer hover:border-n-strong hover:bg-n-alpha-1'"
        @click="startEdit(field, $event)"
      >
        <!-- Label -->
        <span class="text-xs font-medium text-n-slate-9 uppercase tracking-wide">
          {{ field.label }}
        </span>

        <!-- Company domain search field -->
        <template v-if="field.fieldType === 'company_search'">
          <div>
            <input
              v-if="showCompanySearch"
              ref="companySearchInputRef"
              v-model="companySearchQuery"
              type="text"
              placeholder="Type company name…"
              class="w-full px-2 py-1 text-sm border border-woot-400 rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
              @keydown.esc.prevent="closeCompanySearch"
              @click.stop
            />
            <template v-else>
              <span v-if="getFieldValue(field)" class="text-sm text-n-slate-12 mt-0.5 break-words">
                {{ getFieldValue(field) }}
              </span>
              <span v-else class="text-sm text-n-slate-6 mt-0.5">+ Click to search</span>
            </template>
          </div>
        </template>

        <!-- Editing state for other fields -->
        <template v-else-if="isEditing(field)">
          <select
            v-if="field.fieldType === 'list'"
            ref="editInputRef"
            v-model="editValue"
            class="w-full px-2 py-1 text-sm border border-woot-400 rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            @change="saveEdit"
            @keydown.esc.prevent="cancelEdit"
            @blur="saveEdit"
            @click.stop
          >
            <option value="">— clear —</option>
            <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
          </select>
          <input
            v-else
            ref="editInputRef"
            v-model="editValue"
            :type="field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text'"
            class="w-full px-2 py-1 text-sm border border-woot-400 rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            @keydown.enter.prevent="saveEdit"
            @keydown.esc.prevent="cancelEdit"
            @blur="saveEdit"
            @click.stop
          />
        </template>

        <!-- Display state -->
        <template v-else>
          <template v-if="field.isCustom && field.fieldType === 'checkbox'">
            <div class="flex items-center gap-1.5 mt-0.5">
              <span
                class="inline-flex items-center justify-center w-4 h-4 rounded"
                :class="getFieldValue(field) ? 'bg-woot-500 text-white' : 'border border-n-weak'"
              >
                <span v-if="getFieldValue(field)" class="i-lucide-check size-3" />
              </span>
              <span class="text-sm text-n-slate-11">{{ getFieldValue(field) ? 'Yes' : 'No' }}</span>
            </div>
          </template>
          <template v-else-if="field.isCustom && field.fieldType === 'link'">
            <a
              v-if="getFieldValue(field)"
              :href="getFieldValue(field)"
              target="_blank"
              rel="noopener"
              class="text-sm text-woot-500 hover:underline truncate mt-0.5"
              @click.stop
            >{{ getFieldValue(field) }}</a>
            <span v-else class="text-sm text-n-slate-6 mt-0.5">+ Click to add</span>
          </template>
          <template v-else>
            <span v-if="formatValue(field, getFieldValue(field))" class="text-sm text-n-slate-12 mt-0.5 break-words">
              {{ formatValue(field, getFieldValue(field)) }}
            </span>
            <span v-else class="text-sm text-n-slate-6 mt-0.5">+ Click to add</span>
          </template>
        </template>
      </div>
    </div>

    <!-- Company search dropdown (teleported to body to escape overflow clipping) -->
    <Teleport to="body">
      <div
        v-if="showCompanySearch"
        v-click-outside="closeCompanySearch"
        class="fixed bg-n-solid-2 border border-n-weak rounded-lg shadow-xl overflow-y-auto flex flex-col"
        :style="companyDropdownStyle"
      >
        <div v-if="companySearchLoading" class="text-xs text-n-slate-9 px-3 py-2">Searching…</div>
        <template v-else>
          <button
            v-for="company in companySearchResults"
            :key="company.id"
            class="flex flex-col items-start px-3 py-2 hover:bg-n-alpha-2 text-left transition-colors"
            @click="selectCompany(company)"
          >
            <span class="text-sm font-medium text-n-slate-12">{{ company.domain || company.name }}</span>
            <span v-if="company.domain && company.name" class="text-xs text-n-slate-9">{{ company.name }}</span>
          </button>
          <div v-if="!companySearchResults.length && companySearchQuery.length > 0" class="text-xs text-n-slate-9 px-3 py-2">
            No companies found
          </div>
          <div v-if="!companySearchQuery.length" class="text-xs text-n-slate-9 px-3 py-2">
            Start typing to search…
          </div>
        </template>
        <button
          v-if="selectedContact.companyId"
          class="text-xs text-red-500 hover:text-red-600 text-left px-3 py-2 border-t border-n-weak"
          @click="clearCompany"
        >
          Remove company link
        </button>
      </div>
    </Teleport>

    <!-- Company section -->
    <div v-if="selectedContact.companyId" class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-n-slate-12 border-b border-n-weak pb-2">
        Company
      </h4>
      <div
        class="flex items-center gap-3 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-1 cursor-pointer transition-colors"
        @click="openCompany"
      >
        <span class="flex items-center justify-center w-8 h-8 rounded-full bg-woot-50 dark:bg-woot-900/20 text-woot-500 flex-shrink-0">
          <span class="i-lucide-building-2 size-4" />
        </span>
        <span class="text-sm font-medium text-n-slate-12 flex-1 truncate">
          {{ selectedContact.companyDomain || `Company #${selectedContact.companyId}` }}
        </span>
        <span class="i-lucide-arrow-right size-3.5 text-n-slate-7" />
      </div>
    </div>

    <!-- Conversations section -->
    <div v-if="conversations.length" class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-n-slate-12 border-b border-n-weak pb-2">
        Conversations ({{ conversations.length }})
      </h4>
      <div class="flex flex-col gap-2">
        <div
          v-for="conv in conversations.slice(0, 5)"
          :key="conv.id"
          class="flex items-center gap-3 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-1 cursor-pointer transition-colors"
          @click="openConversation(conv.id)"
        >
          <span
            class="text-xs px-2 py-0.5 rounded-full font-medium"
            :class="conv.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-n-alpha-2 text-n-slate-9'"
          >
            {{ conv.status }}
          </span>
          <span class="text-sm text-n-slate-11 flex-1 truncate">
            {{ conv.meta?.channel || conv.inboxId || `#${conv.id}` }}
          </span>
          <span class="text-xs text-n-slate-7">{{ formatDate(conv.createdAt) }}</span>
          <span class="i-lucide-arrow-right size-3.5 text-n-slate-7" />
        </div>
      </div>
      <span
        v-if="conversations.length > 5"
        class="text-xs text-woot-500 cursor-pointer hover:underline"
      >
        View all {{ conversations.length }} conversations
      </span>
    </div>

    <!-- Tickets section -->
    <div v-if="tickets.length || ticketsLoading" class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-n-slate-12 border-b border-n-weak pb-2">
        Tickets ({{ tickets.length }})
      </h4>
      <div v-if="ticketsLoading" class="text-xs text-n-slate-9">Loading tickets…</div>
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="ticket in tickets.slice(0, 5)"
          :key="ticket.id"
          class="flex items-center gap-3 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-1 cursor-pointer transition-colors"
          @click="openTicket(ticket.id)"
        >
          <span
            class="text-xs px-2 py-0.5 rounded-full font-medium capitalize"
            :class="ticketStatusClass(ticket.status)"
          >
            {{ ticket.status }}
          </span>
          <span class="text-sm text-n-slate-11 flex-1 truncate">
            {{ ticket.subject || ticket.title || `Ticket #${ticket.id}` }}
          </span>
          <span class="text-xs text-n-slate-7">{{ formatDate(ticket.created_at || ticket.createdAt) }}</span>
          <span class="i-lucide-arrow-right size-3.5 text-n-slate-7" />
        </div>
      </div>
    </div>
  </div>
</template>
